import React, { useEffect, useMemo, useState } from "react";
import { GetAll } from "adminApi/MonthlyStatisticsApi/MonthlyStatisticsApi";
import DataTable from "Common/DataTable/DataTable";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import feilds from "../json/MonthlyStatistics.json";




const MonthlyStatisticsTable = ({ filters, date }) => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { tabledata } = feilds

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await GetAll(
          {
            month: filters.month,
            year: filters.year,
          },
          controller.signal
        );

        if (res?.success) {
          const data = Array.isArray(res.data) ? res.data : [];
          setRawData(data);
        } else {
          setError(res?.message || "Failed to fetch data");
          setRawData([]);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Something went wrong");
          setRawData([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => controller.abort();
  }, [filters.month, filters.year]);

  /*
   * Filter out date rows with no booking data
   * The API returns all dates of the month; rows with no bookings
   * only have { date, cash: 0, bank: 0, park: 0, amount: 0, total_amount: 0 }
   */
  const filteredData = useMemo(() => {
    return rawData.filter((row) => {
      return Object.entries(row).some(([key, value]) => {
        if (key === 'date') return false; // skip the date field itself
        if (typeof value === 'number') return value !== 0;
        if (typeof value === 'string') return value.trim() !== '' && value !== '0';
        return false;
      });
    });
  }, [rawData]);

  /*
   * Dynamic columns
   */
  const displayFields = useMemo(() => {
    if (!filteredData?.length) {
      return generateDisplayFields(tabledata);
    }

    // Fields that should NOT be treated as room types
    const specialFields = new Set([
      "date",
      "park",
      "amount",
      "cash",
      "bank",
      "total_amount",
    ]);

    // Tax names
    const taxNames = new Set([
      "GST",
      "SGST",
      "CGST",
      "IGST",
      "UTGST",
      "CESS",
      "VAT",
      "Service Tax",
    ]);

    const roomTypes = new Set();
    const taxes = new Set();

    filteredData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (specialFields.has(key)) {
          return;
        }

        if (taxNames.has(key)) {
          taxes.add(key);
        } else {
          roomTypes.add(key);
        }
      });
    });

    // 1. Date
    const dateField = tabledata.filter(
      (field) => field.name === "date"
    );

    // 2. Room Types
    const roomTypeFields = Array.from(roomTypes).map((key) => ({
      name: key,
      label: key,
      type: "number",
    }));

    // 3. Park
    const parkField = tabledata.filter(
      (field) => field.name === "park"
    );

    // 4. Amount
    const amountField = tabledata.filter(
      (field) => field.name === "amount"
    );

    // 5. Taxes
    const taxFields = Array.from(taxes).map((key) => ({
      name: key,
      label: key,
      type: "number",
    }));

    // 6. Total
    const totalField = [
      {
        name: "total_amount",
        label: "Total",
        type: "number",
      },
    ];

    // 7. Cash
    const cashField = tabledata.filter(
      (field) => field.name === "cash"
    );

    // 8. Bank
    const bankField = tabledata.filter(
      (field) => field.name === "bank"
    );

    return generateDisplayFields([
      ...dateField,
      ...roomTypeFields,
      ...parkField,
      ...amountField,
      ...taxFields,
      ...totalField,
      ...cashField,
      ...bankField,
    ]);
  }, [filteredData]);

  console.log("displayFields", displayFields)

  /*
   * Footer Total Row
   */
  const footerRow = useMemo(() => {
    if (!filteredData?.length) {
      return null;
    }

    // rowNo is added by useTableConfig (not part of displayFields)
    const totalRow = { rowNo: "Total" };

    displayFields.forEach((field) => {
      const key = field['accessor']; // accessor = label, matches column id in DataTable

      let total = 0;
      let hasNumber = false;

      filteredData.forEach((row) => {
        const value = row[field['dataKey']];
        if (typeof value === 'number') {
          total += value;
          hasNumber = true;
        } else if (
          typeof value === 'string' &&
          value.trim() !== '' &&
          Number.isFinite(Number(value))
        ) {
          total += Number(value);
          hasNumber = true;
        }
      });

      totalRow[key] = hasNumber ? Number(total.toFixed(2)) : '';
    });

    console.log("totalRow", totalRow)

    return totalRow;
  }, [displayFields, filteredData]);

  /*
   * Table Configuration
   */
  const { formattedData, columns } = useTableConfig({
    data: filteredData,
    canShowAction: false,
    refetch: () => { },
    IsDelete: false,
    IsEdit: false,
    config: {
      entityName: "Monthly Statistics Report",
      editUrl: "",
      idKey: "date",
      deleteApi: () => { },
      displayFields,
    },
  });

  console.log("columns", columns)

  return (
    <DataTable
      columns={columns}
      data={formattedData}
      options={{
        footerRow,
        loading,
        error,

        sortableColumns: [],

        copy: true,
        csv: true,
        pdf: true,
        print: true,

        fileName: "Monthly Statistics Report",
      }}
      pagination={{
        isActive: false,
      }}
      isDate={true}
      date={date}
    />
  );
};

export default MonthlyStatisticsTable;